import type { id } from './id';

/**
 * English.
 *
 * Typed as a complete mapping of every key in `id.ts`: leave one out and the
 * build fails. That is deliberate — a partly translated app is worse than an
 * untranslated one, because the gaps only show up in front of the customer
 * who chose the other language.
 */
export const en: Record<keyof typeof id, string> = {
  // --- Shared chrome ---
  'app.loading': 'Loading…',
  'app.quotaLeft': 'Row quota',
  'nav.accurate': 'Accurate connection',
  'nav.team': 'Users',
  'nav.billing': 'Subscription & quota',
  'nav.account': 'Account & password',
  'nav.logout': 'Sign out',
  'nav.loggingOut': 'Signing out…',
  'quota.exhausted': "This month's row quota is used up — new receipts will be refused.",
  'quota.upgrade': 'Upgrade plan',
  'quota.askAdmin': 'Ask an admin to upgrade the plan.',
  'nav.backAria': 'Back',

  // --- Common actions and words ---
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.add': 'Add',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.search': 'Search',
  'common.loading': 'Loading…',
  'common.saving': 'Saving…',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.required': 'Required',
  'common.unknownError': 'Something went wrong. Please try again.',

  // --- Roles ---
  'role.owner': 'Owner',
  'role.admin': 'Admin',
  'role.supervisor': 'Supervisor',
  'role.staff': 'Staff',

  // --- Login and signup ---
  'login.title': 'Sign in',
  'login.signupTitle': 'Create an account',
  'login.tagline': 'Warehouse goods receiving, connected straight to Accurate Online',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Working…',
  'login.noAccount': "Don't have an account?",
  'login.signupLink': 'Sign up free',
  'login.haveAccount': 'Already have an account?',
  'login.loginLink': 'Sign in here',
  'signup.company': 'Company name',
  'signup.name': 'Your name',
  'signup.submit': 'Sign up',

  // --- Database picker and menu ---
  'home.pickDatabase': 'Choose a database',
  'home.historyOnly': 'Disconnected · history only',
  'home.connectionProblem': 'Connection problem',
  'menu.scan': 'New scan',
  'menu.drafts': 'Resume a draft',
  'menu.history': 'Receipt list',
  'menu.notFound': 'Database not found, or you do not have access to it.',
  'menu.disconnected':
    'This database is no longer connected to Accurate. Its receiving history can still be opened and printed, but new receipts cannot be created until the connection is restored in Settings.',
  // --- Shared status and role labels ---
  'role.adminDesc': 'Admin — manages users and connections',
  'role.supervisorDesc': 'Supervisor — scan, submit and void receipts',
  'role.staffDesc': 'Staff — scan and submit receipts',
  'platformRole.superadmin': 'Superadmin',
  'platformRole.admin': 'Admin',
  'receiptStatus.posted': 'Submitted',
  'receiptStatus.pending': 'Processing',
  'receiptStatus.failed': 'Failed',
  'receiptStatus.voided': 'Voided',
  'billingStatus.trialing': 'Trial',
  'billingStatus.active': 'Active',
  'billingStatus.pastDue': 'Overdue',
  'billingStatus.cancelled': 'Cancelled',
  'billingStatus.paid': 'Paid',
  'billingStatus.pending': 'Awaiting payment',
  'billingStatus.failed': 'Failed',
  'billingStatus.expired': 'Expired',
  'billingStatus.refunded': 'Refunded',
  // --- GRN print ---
  'grn.loadFailed': 'Could not load the document.',
  'grn.preparing': 'Preparing the document…',
  'grn.printTip': 'Tip: turn off {option} in the print dialog for a clean result.',
  'grn.print': 'Print',
  'grn.subtitle': 'Warehouse Goods Receipt',
  'grn.badge': 'Good Receive Note',
  'grn.vendor': 'Supplier',
  'grn.poNumber': 'Purchase order no.',
  'grn.notes': 'Receiving notes',
  'grn.grNumber': 'Receipt no.',
  'grn.date': 'Receipt date',
  'grn.deliveryNote': 'Delivery note no.',
  'grn.warehouse': 'Receiving warehouse',
  'grn.continued': 'continued',
  'grn.colNo': 'No',
  'grn.colSku': 'Item code',
  'grn.colBarcode': 'Barcode / UPC',
  'grn.colItem': 'Item details',
  'grn.colQty': 'Qty received',
  'grn.colUnit': 'Unit',
  'grn.colNotes': 'Item notes',
  'grn.total': 'Total',
  'grn.receivedBy': 'Received by,',
  'grn.receivedBySub': '(Warehouse staff)',
  'grn.deliveredBy': 'Delivered by,',
  'grn.deliveredBySub': '(Courier)',
  'grn.createdBy': 'Created by {name}',
  'grn.accurateNo': 'Accurate no.: {number}',
  'grn.signName': 'Name',
  'grn.signDate': 'Date',
  'grn.notPrintable':
    'Receipt {number} has the status "{status}", so its GRN cannot be printed. Only receipts recorded in Accurate may be signed for.',
  // --- Receipt detail modal ---
  'detail.title': 'Receipt details',
  'detail.voidTitle': 'Void receipt',
  'detail.loading': 'Loading details…',
  'detail.loadFailed': 'Could not load the details.',
  'detail.voidFailed': 'Could not void the receipt.',
  'detail.voidWarning':
    'Document {number} will be deleted from Accurate. Stock and the outstanding quantity on PO {po} return to what they were before this receipt. The record here is kept as "Voided", with its reason.',
  'detail.voidReason': 'Reason for voiding',
  'detail.voidReasonHint':
    'Required, at least 5 characters. This reason is kept permanently on the receipt record.',
  'detail.voidReasonPlaceholder': 'For example: wrong warehouse entered, goods returned to supplier',
  'detail.voiding': 'Voiding…',
  'detail.voidConfirm': 'Yes, void this receipt',
  'detail.failReason': 'Failure reason from Accurate',
  'detail.failNoReason': 'This receipt failed, but Accurate did not send a reason.',
  'detail.voidedLabel': 'Voided',
  'detail.voidedNoReason': 'No reason recorded.',
  'detail.voidedBy': 'by {name}',
  'detail.receiptNo': 'Receipt no.',
  'detail.status': 'Status',
  'detail.date': 'Date',
  'detail.poNo': 'PO no.',
  'detail.vendor': 'Supplier',
  'detail.branch': 'Branch',
  'detail.warehouse': 'Warehouse',
  'detail.deliveryNote': 'Delivery note',
  'detail.database': 'Database',
  'detail.createdBy': 'Created by',
  'detail.accurateNo': 'Accurate no.',
  'detail.notes': 'Notes',
  'detail.items': 'Items ({count})',
  'detail.colItem': 'Item',
  'detail.colOrdered': 'Ordered',
  'detail.colReceived': 'Received',
  'detail.colResult': 'Result',
  // --- Receipt history ---
  'history.loadFailed': 'Could not load the history.',
  'history.loading': 'Loading history…',
  'history.search': 'Search',
  'history.searchPlaceholder': 'Receipt no., supplier, delivery note, PO no.…',
  'history.monthFilter': 'Filter by month',
  'history.empty': 'Nothing found.',
  'history.colNo': 'NO',
  'history.colDate': 'Date',
  'history.colReceipt': 'Receipt no.',
  'history.colVendor': 'Supplier',
  'history.colDeliveryNote': 'Delivery note',
  'history.colWarehouse': 'Warehouse',
  'history.colCreatedBy': 'Created by',
  'history.colItems': 'Items (qty)',
  'history.colStatus': 'Status',
  'history.colActions': 'Actions',
  'history.viewDetail': 'View details',
  'history.viewDetailFor': 'View details for {number}',
  'history.printGrn': 'Print GRN',
  'history.printGrnFor': 'Print GRN {number}',
  // --- Accurate connection settings ---
  'acc.title': 'Accurate Online connection',
  'acc.connected': 'Connected',
  'acc.notConnected': 'Not connected',
  'acc.connectedOk': 'Accurate account connected successfully.',
  'acc.statusFailed': 'Could not load the connection status.',
  'acc.startFailed': 'Could not start the connection process.',
  'acc.listFailed': 'Could not fetch the list of databases.',
  'acc.connectDbFailed': 'Could not connect the database.',
  'acc.saveFailed': 'Could not save the changes.',
  'acc.disconnectFailed': 'Could not disconnect.',
  'acc.removeDbFailed': 'Could not release the database.',
  'acc.account': 'Accurate account',
  'acc.validUntil': 'Connection valid until',
  'acc.lastRefreshed': 'Last refreshed',
  'acc.never': 'Never',
  'acc.autoRefresh':
    'The connection refreshes itself before it expires. You do not need to reconnect unless access is revoked on the Accurate side.',
  'acc.disconnecting': 'Disconnecting…',
  'acc.disconnect': 'Disconnect',
  'acc.redirecting': 'Redirecting…',
  'acc.connectAccount': 'Connect Accurate account',
  'acc.connectedDatabases': 'Connected databases',
  'acc.noDatabases': 'No database in use yet.',
  'acc.accurateId': 'Accurate ID {id}',
  'acc.editEdrc': 'Change ED RC',
  'acc.disconnectShort': 'Release',
  'acc.processing': 'Working…',
  'acc.availableTitle': 'Available in your Accurate account',
  'acc.alreadyConnected': 'Already connected',
  'acc.connect': 'Connect',
  'acc.connecting': 'Connecting…',
  'acc.viewPlans': 'View plans',
  'acc.upgradeNow': 'Upgrade your plan →',
  'acc.connectTitle': 'Connect "{alias}"',
  'acc.edrcField': 'ED RC field',
  'acc.edrcCurrent': 'Currently: {value}',
  'acc.edrcHelp':
    'Choose the Accurate custom field that holds ED RC (minimum remaining shelf life, in months) in this database. It can differ per database.',
  'acc.edrcEditTitle': 'Change ED RC field — {alias}',
  'acc.edrcEditHelp':
    'The Accurate custom field holding ED RC (minimum remaining shelf life, in months). The change applies to future receipts; existing history is unaffected.',
  'acc.confirmDisconnectTitle': 'Disconnect from Accurate?',
  'acc.confirmDisconnectYes': 'Yes, disconnect',
  'acc.confirmDisconnectBody':
    '{count} database(s) will be released and scanning stops until you reconnect. Receiving history is kept.',
  'acc.confirmDisconnectBodyNone':
    'The Accurate token will be deleted and you will need to reconnect before you can work.',
  'acc.confirmDbReleased': 'Databases released',
  'acc.confirmHistoryKept': 'Receiving history',
  'acc.confirmHistoryKeptValue': 'Kept',
  'acc.confirmRemoveTitle': 'Release "{alias}"?',
  'acc.confirmRemoveBody':
    'This database is released from your account and can be connected again at any time — including by another account. Receiving history is kept.',
  // --- Scan and submit ---
  'scan.warehousesFailed': 'Could not load the warehouse list.',
  'scan.draftLoadFailed': 'Could not load the draft.',
  'scan.poFailed': 'Could not load the purchase order.',
  'scan.draftSaved': 'Draft saved.',
  'scan.draftSaveFailed': 'Could not save the draft.',
  'scan.submitFailed': 'Could not submit the receipt.',
  'scan.noDatabase': 'Accurate database not found, or you do not have access to it.',
  'scan.prepareTitle': 'Prepare the receiving document',
  'scan.poLabel': 'Purchase order number',
  'scan.poPlaceholder': 'Scan or type the PO number, then press Enter',
  'scan.searching': 'Searching…',
  'scan.findPo': 'Find PO',
  'scan.contactingAccurate': 'Contacting Accurate…',
  'scan.vendorName': 'Supplier name',
  'scan.shipAddress': 'Receiving address',
  'scan.shipAddressHint': 'Check that the address is correct.',
  'scan.deliveryNote': 'Delivery note / supplier invoice number',
  'scan.warehouse': 'Receiving warehouse',
  'scan.continue': 'Continue',
  'scan.vendor': 'Supplier',
  'scan.poNumber': 'PO number',
  'scan.deliveryNoteShort': 'Delivery note',
  'scan.barcodePlaceholder': 'Scan an item barcode…',
  'scan.tableSearch': 'Search the table…',
  'scan.colItem': 'Item details',
  'scan.colOrdered': 'Qty ordered',
  'scan.colUnit': 'Unit',
  'scan.colReceived': 'Qty received',
  'scan.colStatus': 'Status',
  'scan.colNotes': 'Notes',
  'scan.editQty': 'Change quantity',
  'scan.emptyScanned': '— Nothing scanned yet —',
  'scan.emptySearch': '— No matching rows —',
  'scan.saveDraft': 'Save draft',
  'scan.submit': 'Submit to Accurate',
  'scan.confirmTitle': 'Confirm submission',
  'scan.sending': 'Sending…',
  'scan.confirmSend': 'Yes, send to Accurate',
  'scan.lineCount': 'Line count',
  'scan.totalQty': 'Total quantity',
  'scan.receiveNotes': 'Receiving notes',
  'scan.receiveNotesHint': 'This text is saved as the document description in Accurate Online.',
  'scan.receivedVia': 'Received via Inbound App by {name}',
  'scan.shortLines': '{count} line(s) received short of the ordered quantity.',
  'scan.barcodeNotInPo': 'Barcode {barcode} is not on this PO.',
  'scan.barcodeComplete': 'Every variant of barcode {barcode} has already been received in full.',
  'scan.pickVariant': 'Choose an item variant',
  'scan.qtyTitle': 'Enter received quantity',
  'scan.saveItem': 'Save item',
  'scan.openPhoto': 'Open the original photo in a new tab',
  'scan.noPhoto': 'No photo',
  'scan.maxReceive': 'Max. receivable',
  'scan.remaining': 'Outstanding',
  'scan.received': 'Received',
  'scan.edrcMonths': 'ED RC (months)',
  'scan.itemNotes': 'Item notes',
  'scan.itemNotesPlaceholder': 'e.g. dented packaging, short expiry…',
  'scan.qtyMin': 'Quantity must be at least 1.',
  'scan.qtyMaxExisting': 'This line can total at most {max} {unit}.',
  'scan.qtyMaxRemaining': 'More than the outstanding quantity — at most {max} {unit}.',
  'scan.alreadyInTable': 'This item is already in the table ({qty} {unit}). The value below replaces it.',
  'scan.fulfilmentComplete': 'Complete',
  'scan.fulfilmentShort': 'Short',
  'scan.itemCount': '{count} item(s) ({qty} qty)',
  'scan.confirmWarning':
    'This receipt will be recorded in Accurate Online and will reduce the outstanding order. It cannot be undone from this app.',
  'acc.connectHelp':
    'Connect your Accurate Online account. You will be sent to the official Accurate page to grant access — DR Inbound never sees your Accurate password.',
  'detail.voidAction': 'Void receipt',
  'role.restricted': 'This page is for {roles} only. Ask your company admin if you need access.',
  'role.backHome': 'Back to home',
  // --- Drafts ---
  'drafts.loadFailed': 'Could not load the drafts.',
  'drafts.deleteFailed': 'Could not delete the draft.',
  'drafts.searching': 'Looking for drafts…',
  'drafts.title': 'Saved drafts',
  'drafts.empty': 'No drafts',
  'drafts.emptyHint': 'Everything in this database has been completed.',
  'drafts.deleting': 'Deleting…',
  'drafts.confirmTitle': 'Delete this draft?',
  'drafts.confirmBody': 'This draft will be discarded and cannot be brought back.',
  'drafts.poNumber': 'PO number',
  'drafts.vendor': 'Supplier',
  'drafts.scanned': 'Scanned so far',
  'drafts.scannedValue': '{lines} line(s) · {qty} qty',
  'drafts.warehouse': 'Warehouse',
  'drafts.vendorInline': 'Supplier: {name}',
  'drafts.deliveryNoteInline': 'Delivery note: {number}',

  // --- Account ---
  'account.failed': 'Could not change the password.',
  'account.myAccount': 'My account',
  'account.email': 'Email',
  'account.role': 'Role',
  'account.organisation': 'Organisation',
  'account.changePassword': 'Change password',
  'account.changed': 'Password changed.',
  'account.current': 'Current password',
  'account.new': 'New password',
  'account.mustDiffer': 'Must differ from the old password.',
  'account.repeat': 'Repeat the new password',
  'account.mismatch': 'Does not match.',

  // --- Email verification ---
  'verify.failed': 'Verification failed.',
  'verify.resendFailed': 'Could not resend.',
  'verify.verifying': 'Verifying your email…',
  'verify.verified': 'Email verified',
  'verify.toAccount': 'Go to your account',
  'verify.failedTitle': 'Verification failed',
  'verify.toLogin': 'Go to sign in',
  'verify.title': 'Verify your email',
  'verify.sentTo': 'We sent a verification link to {email}. Open it to activate your account and start the trial.',
  'verify.trialNote': 'The trial starts only once the email is verified.',
  'verify.devMode': 'Development mode',
  'verify.devNote': 'No mail service is configured, so the link is shown here:',
  'verify.sending': 'Sending…',
  'verify.resend': 'Resend the link',

  // --- Mock checkout ---
  'mock.notFound': 'Invoice not found.',
  'mock.markFailed': 'Could not mark it paid.',
  'mock.loading': 'Loading the invoice…',
  'mock.banner': 'Simulated payment page — development only',
  'mock.invoice': 'Invoice',
  'mock.plan': 'Plan',
  'mock.price': 'Price',
  'mock.total': 'Total',
  'mock.alreadyPaid': 'This invoice is already paid.',
  'mock.processing': 'Working…',
  'mock.markPaid': 'Mark paid (DEV)',

  // --- Common additions ---
  'common.saveFailed': 'Could not save.',
  'common.never': 'Never',
  'common.you': '(You)',
  'common.processing': 'Working…',
  'common.email': 'Email',
  'common.password': 'Password',
  'common.newPassword': 'New password',

  // --- Confirm dialog defaults ---
  'confirm.acknowledge': 'Got it',
  'confirm.continue': 'Continue',

  // --- Team management ---
  'team.loadFailed': 'Could not load the user list.',
  'team.changeSaved': 'Changes for {name} saved.',
  'team.saveChangeFailed': 'Could not save the changes.',
  'team.deactivateTitle': 'Deactivate {name}?',
  'team.deactivate': 'Deactivate',
  'team.activate': 'Activate',
  'team.deactivateBody': 'They can no longer sign in, effective immediately. Receipts already created stay recorded under their name.',
  'team.seatUsedLabel': 'Seats used',
  'team.seatDecreases': 'one fewer',
  'team.reactivableLabel': 'Can be reactivated',
  'team.reactivableValue': 'yes, if a seat is free',
  'team.heading': 'Users',
  'team.activeUnlimited': '{count} active users · unlimited',
  'team.seatsUsed': '{used} of {max} seats used',
  'team.add': 'Add User',
  'team.atLimit': 'Every seat on your plan is taken. Deactivate users who no longer work here, or upgrade the plan to add seats.',
  'team.loadingUsers': 'Loading users…',
  'team.colName': 'Name',
  'team.colEmail': 'Email',
  'team.colRole': 'Role',
  'team.colLastLogin': 'Last login',
  'team.colActions': 'Actions',
  'team.resetPassword': 'Reset password',
  'team.userSaved': "{name}'s details saved.",
  'team.passwordReset': "{name}'s password has been reset. Ask them to change it in the Account menu.",
  'team.userAdded': '{name} added.',
  'team.addFailed': 'Could not add the user.',
  'team.adding': 'Adding…',
  'team.emailHintNew': 'Used to sign in. Must not already be registered.',
  'team.initialPassword': 'Initial password',
  'team.initialPasswordHint': 'Share it with the user, and ask them to change it after signing in.',
  'team.editTitle': 'Edit User',
  'team.emailHintEdit': 'Used to sign in. Changing it changes how they log in.',
  'team.resetFailed': 'Could not reset the password.',
  'team.resetTitle': 'Reset Password — {name}',
  'team.resetSubmit': 'Reset password',
  'team.resetWarnPre': 'Share this password with {name} through a secure channel, and ask them to change it themselves in the ',
  'team.resetWarnPost': ' menu.',

  // --- Billing ---
  'billing.loadFailed': 'Could not load subscription data.',
  'billing.invoiceCreatedTitle': 'Invoice Created',
  'billing.invoiceCreatedBody': 'Please make the bank transfer and send proof to the DR Inbound team to activate your plan.',
  'billing.invoiceNo': 'Invoice No.',
  'billing.checkoutFailed': 'Could not create the invoice.',
  'billing.loading': 'Loading subscription…',
  'billing.heading': 'Subscription & Usage',
  'billing.readonlyNote': 'You can view the plan and usage, but purchases and renewals are handled by the account owner.',
  'billing.trialPeriod': 'Trial period',
  'billing.activePlan': 'Active plan',
  'billing.freeTrial': 'Free Trial',
  'billing.trialEnds': 'Ends {date}',
  'billing.quotaResets': 'Quota resets {date}',
  'billing.activeUntil': 'active until {date}',
  'billing.rowsUsed': 'Rows used',
  'billing.trialQuotaNote': 'Trial quota of {rows} rows — enough to try the receiving flow. Choose a plan below for the full quota.',
  'billing.exhausted': "This month's quota is used up. New receipts will be rejected until you upgrade the plan or the next period begins.",
  'billing.warnRemaining': "{rows} rows left this month. Consider upgrading so receiving doesn't stop.",
  'billing.perMonth': '/month',
  'billing.taxLine': '+ VAT {tax} — total {total}',
  'billing.rowsPerMonth': 'product rows / month',
  'billing.rowsPerDay': 'rows / working day',
  'billing.dbUnlimited': 'Unlimited Accurate databases',
  'billing.dbCount': 'Accurate databases',
  'billing.usersUnlimited': 'Unlimited users',
  'billing.usersCount': 'users',
  'billing.renew': 'Renew',
  'billing.choosePlan': 'Choose Plan',
  'billing.ownerOnly': 'Only the account owner can purchase a plan',
  'billing.invoiceHistory': 'Invoice History',
  'billing.colPeriod': 'Period',
  'billing.colTotal': 'Total',
  'billing.colStatus': 'Status',
  'billing.colPlan': 'Plan',
  'billing.pay': 'Pay',
  'billing.pdfTitle': 'Download invoice {number} (PDF)',

  // --- Danger zone — account deletion ---
  'danger.scheduleFailed': 'Could not schedule the deletion.',
  'danger.cancelFailed': 'Could not cancel the deletion.',
  'danger.scheduledTitle': 'Account Scheduled for Deletion',
  'danger.scheduledAlertPre': 'All data for this organisation will be permanently deleted on ',
  'danger.scheduledAlertPost': '. The Accurate connection has been severed and the app cannot be used until the deletion is cancelled.',
  'danger.cancelling': 'Cancelling…',
  'danger.cancelDeletion': 'Cancel Deletion',
  'danger.heading': 'Delete Account',
  'danger.intro': 'Deletes the organisation along with all users, drafts and receiving history. The Accurate connection is severed immediately; data is permanently deleted after the grace period.',
  'danger.deleteButton': 'Delete Organisation Account',
  'danger.deleteTitle': 'Delete Organisation Account',
  'danger.confirmDelete': 'Delete Account',
  'danger.warnPre': 'This action deletes ',
  'danger.warnBold': 'the entire receiving history',
  'danger.warnPost': ' of this organisation. Once the grace period ends, the data cannot be recovered.',
  'danger.immediate': 'What happens immediately: the Accurate connection is severed and the databases are released so another account can use them. You can still cancel during the grace period.',
  'danger.passwordLabel': 'Your password',
  'danger.passwordHint': 'Re-confirmed for an action this important.',
  'danger.confirmNameLabel': 'Type the company name to confirm',
  'danger.confirmNameHint': 'Exactly: {name}',

  // --- Platform admin console ---
  'admin.navTenant': 'Tenants',
  'admin.navAdmin': 'Administrators',
  'admin.navPassword': 'Password',
  'admin.loginFailed': 'Could not sign in.',
  'admin.loginTitle': 'Platform Administration',
  'admin.loginSubtitle': 'Internal area. Not the customer sign-in page.',
  'admin.checking': 'Checking…',
  'admin.signIn': 'Sign in',
  'admin.changePwFailed': 'Could not change the password.',
  'admin.changePwTitle': 'Change Password',
  'admin.forcedNote': 'This account was created with an initial password from the server configuration. Change it before you can use the administration area.',
  'admin.pwUpdated': 'Password updated.',
  'admin.currentPassword': 'Current password',
  'admin.repeatNewPassword': 'Repeat new password',
  'admin.mismatch': "Doesn't match.",
  'admin.tenantsLoadFailed': 'Could not load tenants.',
  'admin.searchTenants': 'Search by organisation name or user email…',
  'admin.loadingTenants': 'Loading tenants…',
  'admin.noTenants': 'No matching tenants.',
  'admin.colOrg': 'Organisation',
  'admin.colOwner': 'Owner',
  'admin.colPlan': 'Plan',
  'admin.colUsers': 'Users',
  'admin.colQuota': 'Quota',
  'admin.colStatus': 'Status',
  'admin.colCreated': 'Created',
  'admin.deletedOn': 'deleting {date}',
  'admin.actionFailed': 'The action failed.',
  'admin.deletionCancelled': 'Deletion cancelled.',
  'admin.cancelDeletion': 'Cancel deletion',
  'admin.suspendTitle': 'Suspend {name}?',
  'admin.suspend': 'Suspend',
  'admin.suspendBody': 'All users of this organisation can no longer sign in, effective immediately. No data is deleted and the suspension can be lifted at any time.',
  'admin.suspensionLifted': 'Suspension lifted.',
  'admin.tenantSuspended': 'Tenant suspended.',
  'admin.liftSuspension': 'Lift suspension',
  'admin.scheduleDeleteTitle': 'Schedule deletion of {name}?',
  'admin.scheduleDelete': 'Schedule deletion',
  'admin.scheduleDeleteBody': 'The Accurate connection is severed immediately and the databases released. Data is permanently deleted after a 30-day grace period, and can be cancelled before then.',
  'admin.deleteReason': 'Deleted by a platform administrator',
  'admin.deletionScheduled': 'Deletion scheduled.',
  'admin.deleteTenant': 'Delete tenant',
  'admin.metaLine': '{email} · created {date} · status ',
  'admin.scheduledPermanent': 'Scheduled for permanent deletion on {date}.',
  'admin.subscription': 'Subscription',
  'admin.rowsPerMonth': 'Rows/month',
  'admin.ends': 'Ends',
  'admin.noSubscription': 'No subscription yet.',
  'admin.databases': 'Accurate databases',
  'admin.notConnected': 'Not connected.',
  'admin.released': '(released)',
  'admin.users': 'Users',
  'admin.inactiveBadge': 'Inactive',
  'admin.unverifiedBadge': 'Unverified',
  'admin.userSuspended': 'User suspended.',
  'admin.userActivated': 'User activated.',
  'admin.invoices': 'Invoices',
  'admin.noInvoices': 'No invoices yet.',
  'admin.invoiceMarkedPaid': 'Invoice {number} marked paid.',
  'admin.markPaid': 'Mark paid',
  'admin.recentActivity': 'Recent activity',
  'admin.adminsLoadFailed': 'Could not load administrators.',
  'admin.adminsHeading': 'Platform Administrators',
  'admin.adminsIntro': 'A superadmin can mark invoices paid, delete tenants, and manage other administrators.',
  'admin.addAdmin': 'Add Admin',
  'admin.mustChangeBadge': 'password not changed',
  'admin.roleChanged': "{name}'s role changed.",
  'admin.deactivateTitle': 'Deactivate {name}?',
  'admin.deactivateBody': 'They lose access to the administration area, effective immediately.',
  'admin.adminDeactivated': 'Administrator deactivated.',
  'admin.adminActivated': 'Administrator activated.',
  'admin.deleteAdminTitle': 'Delete {name}?',
  'admin.deletePermanent': 'Delete permanently',
  'admin.deleteAdminBody': 'This administrator account is permanently deleted. Their actions remain recorded in the audit log.',
  'admin.adminDeleted': '{name} deleted.',
  'admin.deleteFailed': 'Could not delete.',
  'admin.userDataSaved': "{name}'s details saved.",
  'admin.passwordResetMustChange': "{name}'s password has been reset. They must change it at next sign-in.",
  'admin.adminAddedNote': '{name} added. They must change their password at first sign-in.',
  'admin.addAdminFailed': 'Could not add the administrator.',
  'admin.addAdminTitle': 'Add Administrator',
  'admin.initialPasswordHint': 'They must change it at first sign-in.',
  'admin.editAdminTitle': 'Edit Administrator',
  'admin.adminEmailHint': 'Used to sign in. Must not already belong to another administrator.',
  'admin.resetWarn': 'Share this password with {name} through a secure channel. They must change it at their next sign-in, and their old sessions stay locked until they do.',

  // --- Tenant status badges ---
  'tenantStatus.active': 'active',
  'tenantStatus.trialing': 'trialing',
  'tenantStatus.pastDue': 'past due',
  'tenantStatus.suspended': 'suspended',
  'tenantStatus.closing': 'closing',
  'tenantStatus.pending': 'pending',
  'tenantStatus.cancelled': 'cancelled',

  // --- Server errors — fetch layer & generic ---
  'err.network': 'Lost the connection to the server. Check your network.',
  'err.invalidResponse': 'The server returned an invalid response.',
  'err.requestFailed': 'Request failed (HTTP {status}).',
  'err.invalidData': 'The data sent is not valid.',
  'err.system': 'A system error occurred. Please try again.',
  'err.notFoundEndpoint': 'Endpoint not found.',

  // --- Server errors — password strength ---
  'err.pw.tooShort': 'Password must be at least 10 characters.',
  'err.pw.tooLong': 'Password must be at most 200 characters.',
  'err.pw.edgeSpace': 'Password cannot start or end with a space.',
  'err.pw.tooGuessable': 'Password is too easy to guess.',

  // --- Server errors — authentication ---
  'err.auth.emailTakenLogin': 'This email is already registered. Please sign in.',
  'err.auth.badCredentials': 'Incorrect email or password.',
  'err.auth.userInactive': 'Your account is not active yet. Contact your company admin.',
  'err.auth.tenantInactive': 'Your organisation account is deactivated. Contact the DR Inbound team.',
  'err.auth.oldPasswordWrong': 'The old password is incorrect.',
  'err.auth.newPasswordSame': 'The new password must differ from the old one.',
  'err.auth.passwordWrong': 'Incorrect password.',
  'err.auth.emailTaken': 'This email is already registered.',
  'err.auth.sessionExpired': 'Your session has ended. Please sign in again.',
  'err.auth.accountInactive': 'Your account is inactive. Contact an admin.',
  'err.auth.passwordChanged': "This account's password was changed. Please sign in again.",
  'err.auth.tenantInactiveLong': 'Your organisation account is deactivated. Contact the DR Inbound team to reactivate it.',
  'err.auth.verifyFirst': 'Verify your email first to start using the app.',
  'err.auth.closingScheduled': 'This account is scheduled for deletion. Cancel the deletion in Settings to use the app again.',
  'err.auth.notLoggedIn': 'Not signed in.',
  'err.auth.forbidden': "You don't have permission for this action.",

  // --- Server errors — account, users, databases ---
  'err.account.notFound': 'Account not found.',
  'err.account.nameMismatch': "The company name doesn't match. Type exactly: {name}",
  'err.user.cannotDeactivateSelf': "You can't deactivate your own account.",
  'err.user.useAccountPageForSelf': 'Use the Account page to change your own password.',
  'err.user.notFoundOrImmutable': 'User not found, or cannot be changed.',
  'err.db.notFoundOrNoAccess': "Accurate database not found, or you don't have access.",

  // --- Server errors — platform admin & verification ---
  'err.admin.sessionExpired': 'Your admin session has ended. Please sign in again.',
  'err.admin.inactive': 'This administrator account is inactive.',
  'err.admin.changePasswordFirst': 'Change your password first.',
  'err.admin.superadminOnly': 'This action is for superadmins only.',
  'err.verify.invalidLink': 'The verification link is invalid or has expired. Request a new one.',

  // --- Server errors — platform admin actions ---
  'err.admin.badCredentials': 'Incorrect email or password.',
  'err.admin.currentPasswordWrong': 'The current password is incorrect.',
  'err.admin.newPasswordSame': 'The new password must differ from the old one.',
  'err.platform.noSuperadminLeft': 'This action would leave the platform with no active superadmin. Appoint another superadmin first.',
  'err.platform.tenantNotFound': 'Tenant not found.',
  'err.platform.tenantClosing': 'This tenant is being deleted.',
  'err.platform.userNotFound': 'User not found.',
  'err.platform.invoiceNotFound': 'Invoice not found.',
  'err.platform.adminEmailTaken': 'This email is already registered as an administrator.',
  'err.platform.cannotChangeOwnRole': "You can't change your own role or account status.",
  'err.platform.adminEmailUsed': 'This email is already used by another administrator.',
  'err.platform.adminNotFound': 'Administrator not found.',
  'err.platform.usePasswordMenuForSelf': 'Use the Password menu to change your own password.',
  'err.platform.cannotDeleteSelf': "You can't delete your own account.",

  // --- Server errors — Accurate connection ---
  'err.accurate.dbNotFound': 'No Accurate database found for your account.',
  'err.accurate.dbDisabled': 'The connection to database “{alias}” is disabled.',
  'err.accurate.noActiveConnection': 'No active Accurate connection yet. Connect an Accurate account first.',
  'err.accurate.reconnectNeeded': 'The Accurate connection needs reconnecting. Open Settings → Accurate Connection.',
  'err.accurate.unreadable': "The Accurate connection can't be read (the encryption key changed). Reconnect your Accurate account.",
  'err.accurate.expiredManual': "The Accurate connection expired and can't be refreshed automatically. Reconnect it.",
  'err.accurate.connectionNotFound': 'Accurate connection not found.',
  'err.accurate.refreshUnreachable': "Accurate can't be reached to refresh the connection right now. Try again shortly — you don't need to reconnect.",
  'err.accurate.refreshFailed': 'Could not refresh the Accurate connection: {message}. Please reconnect.',
  'err.accurate.openUnreachable': 'Could not reach Accurate to open the database.',
  'err.accurate.openInvalidResponse': 'Accurate returned an invalid response while opening the database.',
  'err.accurate.openFailed': 'Could not open the Accurate database: {reason}',
  'err.accurate.listUnreachable': 'Could not fetch the database list from Accurate.',
  'err.accurate.listFailed': 'Could not fetch the database list: {reason}',
  'err.accurate.rejected': "Accurate rejected the connection and it can't be refreshed automatically. Reconnect your Accurate account in Settings. This can happen when the same Accurate account is reconnected from another app or environment.",
  'err.accurate.poNotFound': 'Purchase order “{po}” not found.',
  'err.accurate.dbClaimedByOther': 'Accurate database “{alias}” is already connected to another DR Inbound account. A database can belong to only one account. Disconnect it from the old account first, or contact the DR Inbound team.',
  'err.accurate.noChanges': 'No changes were sent.',
  'err.accurate.dbNotFoundShort': 'Database not found.',

  // --- Server errors — receiving ---
  'err.receipt.notFound': 'Receipt not found.',
  'err.receipt.alreadyVoided': 'Receipt {gr} is already voided.',
  'err.receipt.priorInterrupted': 'Submission {gr} was interrupted and its document was not created in Accurate. The quota has been refunded. Start the receipt again to get a new number.',
  'err.receipt.priorProcessing': 'The previous submission is still processing. Wait a moment, then check the Receipts list.',
  'err.receipt.priorFailed': 'This submission already failed once ({reason}). Start the receipt again to get a new number.',
  'err.receipt.sendUnknown': "The connection to Accurate dropped while sending {gr}, and its status can't be confirmed yet. Do NOT resend this receipt — the system will check it itself. Open the Receipts list in a few minutes to see the result.",
  'err.receipt.sendFailed': 'Submission {gr} to Accurate failed and its document was not created. The record is saved as failed and the quota refunded.',
  'err.receipt.rejected': 'Accurate rejected the receipt: {message}. The record is saved as failed under number {gr} and the quota refunded.',
  'err.receipt.stillProcessingVoid': 'This receipt is still processing. Wait until it finishes before voiding.',
  'err.receipt.onlyPostedVoidable': 'Only successfully submitted receipts can be voided.',
  'err.receipt.docNotFoundInAccurate': 'Document {gr} was not found in Accurate. It may have been deleted there. Contact an admin to reconcile the records.',
  'err.receive.nothingScanned': 'No items have been scanned yet.',
  'err.receive.lineNotInPo': 'That line item is not on PO {po} (id {id}). The PO may have changed in Accurate — reload the PO.',
  'err.receive.lineDuplicated': 'Line “{item}” was submitted more than once.',
  'err.receive.qtyPositive': 'The quantity for “{item}” must be greater than 0.',
  'err.receive.lineFull': '“{item}” has already been fully received ({qty} {unit}).',
  'err.receive.lineOverRemaining': '“{item}”: only {remaining} {unit} can still be received, not {qty}.',

  // --- Server errors — tenant, drafts, billing ---
  'err.tenant.notFound': 'Organisation not found.',
  'err.tenant.deletionAlreadyScheduled': 'Account deletion is already scheduled.',
  'err.tenant.noDeletionScheduled': 'No account deletion is scheduled.',
  'err.tenant.deletionByPlatform': "This account deletion was carried out by the DR Inbound team and can't be cancelled here. Contact the DR Inbound team.",
  'err.draft.notFound': 'Draft not found.',
  'err.draft.notFoundOrProcessed': 'Draft not found, or already processed.',
  'err.invoice.notFound': 'Invoice not found.',
  'err.invoice.notFoundNumbered': 'Invoice {number} not found.',
  'err.ipaymu.unreachable': 'Could not reach the payment provider.',
  'err.ipaymu.createFailed': 'Could not create the invoice: {reason}',
  'err.limits.databases': 'The {plan} plan covers only {max} Accurate databases ({current} already in use). Upgrade the plan to add more.',
  'err.limits.seats': 'The {plan} plan covers only {max} active users ({current} already in use). Deactivate another user or upgrade the plan.',
  'err.limits.planNotFound': 'Plan “{code}” not found.',
  'err.usage.subscriptionInactive': 'Your subscription is inactive. Open the Subscription page to reactivate it.',
  'err.usage.pastDue': 'Subscription payment is past due. Complete the payment to keep receiving.',
  'err.usage.quotaExceeded': "This month's quota is used up: {used} of {limit} rows used, and this transaction needs {needed}. Upgrade the plan to continue.",
  'err.usage.quotaExhausted': "This month's row quota is used up. Upgrade the plan to keep receiving.",

  // --- Server errors — field validation (Zod) ---
  'err.v.emailFormat': 'Invalid email format',
  'err.v.passwordRequired': 'Password is required',
  'err.v.companyNameMin': 'Company name must be at least 2 characters',
  'err.v.nameMin': 'Name must be at least 2 characters',
  'err.v.reasonRequired': 'A reason is required',
  'err.v.poRequired': 'PO number is required',
  'err.v.monthFormat': 'Month must be in YYYY-MM format',
  'err.v.voidReasonMin': 'The cancellation reason must be at least 5 characters',
  'err.v.deliveryNoteRequired': 'Delivery note number is required',
  'err.v.nothingScanned': 'No items have been scanned',
  'err.v.idempotencyUuid': 'idempotencyKey must be a UUID',
  'err.v.edRcField': 'The ED RC field must be numericField1..numericField10',

  // --- Server success messages ---
  'ok.emailVerifiedTrial': 'Email verified. Your trial starts now.',
  'ok.emailVerified': 'Email verified.',
  'ok.emailAlreadyVerified': 'Your email is already verified.',
  'ok.verificationResent': 'A new verification link has been sent.',
  'ok.account.deletionScheduled': 'The account is scheduled for permanent deletion on {date}. The Accurate connection has been severed. You can cancel before that date.',
  'ok.accurate.disconnected': 'Accurate connection disconnected.',
  'ok.accurate.disconnectedWithDb': 'Accurate connection disconnected. {count} database(s) released.',

  // --- Signup form hints ---
  'signup.passwordHint': "At least 10 characters. We'll send a verification link to your email.",
  'signup.companyPlaceholder': 'Acme Trading Co.',

  // --- UI primitives & misc ---
  'scan.chooseWarehouse': '— Choose destination warehouse —',
  'ui.showPassword': 'Show password',
  'ui.hidePassword': 'Hide password',
  'ui.show': 'Show',
  'ui.hide': 'Hide',

  // --- Remaining screen strings ---
  'account.name': 'Name',
  'account.managedByAdmin': 'Your name, email and role are managed by your organisation admin.',
  'mock.vat': 'VAT',
  'mock.disabled': 'Payment simulation is off. Set BILLING_PROVIDER=mock in the development environment.',
  'scan.colActions': 'Actions',
  'scan.poLineCount': '{count} line items on this PO.',
  'scan.multiMatchNote': 'This barcode matches several PO lines — e.g. colour/size variants, or a bonus line. Pick the right one.',
  'app.footer': 'DR Inbound · connected to Accurate Online',
  'verify.successSuffix': 'has been verified.',
  'verify.trialStartedAppend': ' Your trial has started.',
  'verify.linkOnceNote': 'The link works once and expires after 24 hours. Sign in to your account to request a new one.',
  'acc.edrcUpdated': 'The ED RC field for “{alias}” was updated to {field}.',

  // --- Home & drafts inline strings ---
  'home.noDatabase': 'No Accurate database is connected yet.',
  'home.openSettingsPre': 'Open ',
  'home.settingsLink': 'Settings → Accurate Connection',
  'home.openSettingsPost': ' to connect your Accurate account.',
  'home.askAdminConnect': 'Ask your company admin to connect one.',
  'drafts.warehouseLabel': 'Warehouse:',
  'drafts.lineSummary': '{lines} lines · {qty} units scanned',
  'drafts.byInline': 'by {name}',

  // --- Sweep 2 fixes ---
  'acc.dbConnected': 'Database “{alias}” connected successfully.',
  'history.justPostedPre': 'Receipt ',
  'history.justPostedPost': ' was successfully sent to Accurate.',

  // --- Billing plan tiers ---
  'billing.upgrade': 'Upgrade Plan',
  'billing.belowCurrent': 'Below your active plan',
  'billing.upgradeHint': "Quota rises immediately; your next invoice uses the new plan's price.",
  'err.billing.noDowngrade': "You can't downgrade from your active plan. To downgrade, contact the DR Inbound team.",

  // --- Forgot / reset password ---
  'ok.resetLinkSent': "If the email is registered, we've sent a password reset link.",
  'ok.passwordReset': 'Your password has been reset. Please sign in with your new password.',
  'err.reset.invalidLink': 'The password reset link is invalid or has expired. Request a new one.',
  'login.forgotLink': 'Forgot password?',
  'forgot.title': 'Forgot Password',
  'forgot.intro': "Enter your account email. If it's registered, we'll send a link to create a new password.",
  'forgot.submit': 'Send Link',
  'forgot.sending': 'Sending…',
  'forgot.sentTitle': 'Check Your Email',
  'forgot.backToLogin': 'Back to Sign In',
  'forgot.devNote': 'Development mode — the reset link is shown here:',
  'forgot.failed': 'Could not send the link. Try again.',
  'reset.title': 'Reset Password',
  'reset.intro': 'Create a new password for your account.',
  'reset.passwordHint': 'At least 10 characters.',
  'reset.submit': 'Save Password',
  'reset.doneTitle': 'Password Updated',
  'reset.toLogin': 'Go to Sign In',
  'reset.invalidTitle': 'Invalid Link',
  'reset.noToken': 'The link is incomplete. Request a new one from the Forgot Password page.',
  'reset.failed': 'Could not reset the password.',

  // --- Account profile edit & invoice table ---
  'account.editProfile': 'Edit Profile',
  'account.profileSaved': 'Profile updated.',
  'account.profileFailed': 'Could not update the profile.',
  'billing.downloadPdf': 'Download PDF',
  'billing.prev': 'Previous',
  'billing.next': 'Next',
  'billing.pageOf': 'Page {page} of {total}',
  'err.v.tokenRequired': 'A token is required',
  'err.invoice.notPaid': 'The invoice can only be downloaded after it is paid.',
};
